/**
 * SaveStatusIndicator - Google Docs style save status indicator
 *
 * Displays save status with icons in the header area:
 * - saving: Spinning refresh icon
 * - saved: Checkmark icon that fades out after 2 seconds
 * - pending/offline/error: Appropriate icons with persistent display
 *
 * @module features/storage/components/SaveStatusIndicator
 * @see SPEC-006-background-cloud-save-queue
 */

import { memo, useEffect, useState, useRef } from 'react';
import { RefreshCw, Check, AlertCircle, WifiOff, Clock } from 'lucide-react';
import type { SaveStatus } from '../types';
import { useTypedTranslation } from '../../../i18n';

/**
 * Props for SaveStatusIndicator component
 */
export interface SaveStatusIndicatorProps {
  /** Current save status */
  status: SaveStatus;
  /** Optional additional CSS classes */
  className?: string;
  /** Optional click handler (e.g., for retry on error) */
  onClick?: () => void;
}

/**
 * SaveStatusIndicator component - Google Docs style
 *
 * Shows a minimal icon-based indicator:
 * - Spinning refresh icon when saving
 * - Checkmark that fades out when saved
 * - Persistent icons for error/offline states
 */
export const SaveStatusIndicator = memo(function SaveStatusIndicator({
  status,
  className = '',
  onClick,
}: SaveStatusIndicatorProps) {
  const { t } = useTypedTranslation();
  const [isVisible, setIsVisible] = useState(false);
  const [isFadingOut, setIsFadingOut] = useState(false);
  const fadeTimeoutRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const hideTimeoutRef = useRef<ReturnType<typeof setTimeout> | null>(null);

  // Clear timeouts on unmount
  useEffect(() => {
    return () => {
      if (fadeTimeoutRef.current) clearTimeout(fadeTimeoutRef.current);
      if (hideTimeoutRef.current) clearTimeout(hideTimeoutRef.current);
    };
  }, []);

  // Handle visibility based on status
  useEffect(() => {
    // Clear existing timeouts
    if (fadeTimeoutRef.current) clearTimeout(fadeTimeoutRef.current);
    if (hideTimeoutRef.current) clearTimeout(hideTimeoutRef.current);

    if (status === 'saving' || status === 'pending') {
      // Show immediately when saving/pending
      setIsVisible(true);
      setIsFadingOut(false);
    } else if (status === 'saved') {
      // Show checkmark, then fade out after 2 seconds
      setIsVisible(true);
      setIsFadingOut(false);

      fadeTimeoutRef.current = setTimeout(() => {
        setIsFadingOut(true);
      }, 2000);

      hideTimeoutRef.current = setTimeout(() => {
        setIsVisible(false);
        setIsFadingOut(false);
      }, 2500); // 2s display + 0.5s fade
    } else if (status === 'error' || status === 'offline') {
      // Always show error/offline states
      setIsVisible(true);
      setIsFadingOut(false);
    }
  }, [status]);

  // Don't render if not visible
  if (!isVisible) {
    return null;
  }

  // Get icon and styles based on status
  const getStatusConfig = () => {
    switch (status) {
      case 'saving':
        return {
          icon: <RefreshCw size={16} className="animate-spin" />,
          color: 'var(--color-text-secondary)',
          title: t('saveStatus.saving'),
          ariaLabel: t('saveStatus.ariaLabel.saving'),
        };
      case 'saved':
        return {
          icon: <Check size={16} />,
          color: 'var(--color-accent-green, #22c55e)',
          title: t('saveStatus.saved'),
          ariaLabel: t('saveStatus.ariaLabel.saved'),
        };
      case 'pending':
        return {
          icon: <Clock size={16} />,
          color: 'var(--color-accent-yellow, #eab308)',
          title: t('saveStatus.pending'),
          ariaLabel: t('saveStatus.ariaLabel.pending'),
        };
      case 'offline':
        return {
          icon: <WifiOff size={16} />,
          color: 'var(--color-text-secondary)',
          title: t('saveStatus.offline'),
          ariaLabel: t('saveStatus.ariaLabel.offline'),
        };
      case 'error':
        return {
          icon: <AlertCircle size={16} />,
          color: 'var(--color-accent-red, #ef4444)',
          title: t('saveStatus.error'),
          ariaLabel: t('saveStatus.ariaLabel.error'),
        };
      default:
        return null;
    }
  };

  const config = getStatusConfig();
  if (!config) return null;

  const isInteractive = status === 'error' && onClick;

  const containerStyle: React.CSSProperties = {
    display: 'inline-flex',
    alignItems: 'center',
    justifyContent: 'center',
    padding: '4px',
    borderRadius: 'var(--radius-sm, 4px)',
    color: config.color,
    transition: 'opacity 0.5s ease-in-out',
    opacity: isFadingOut ? 0 : 1,
    cursor: isInteractive ? 'pointer' : 'default',
  };

  const Element = isInteractive ? 'button' : 'div';

  return (
    <Element
      className={className}
      style={containerStyle}
      aria-label={config.ariaLabel}
      aria-live="polite"
      aria-atomic="true"
      role={isInteractive ? 'button' : 'status'}
      onClick={isInteractive ? onClick : undefined}
      title={config.title}
    >
      {config.icon}
    </Element>
  );
});

export default SaveStatusIndicator;
